// Tax Flow — shared dual-currency price presentation components.
// Consistent across Knowledge Retrieval, Estimate Builder, and Engagement Preview.
// Presentation-layer only. Canonical price is emphasized; equivalent is informational.
import { fmtAmount, equivalentFor, FX_DISCLAIMER, formatRateDate } from "@/lib/currency";

// Inline dual price: canonical (emphasized) with the equivalent beneath / beside it.
// `layout`: "stacked" (default) or "inline".
export function DualPrice({ value, currency = "USD", rate, showThb, layout = "stacked",
                            strong = true, muted = false, className = "", testid }) {
  const canonical = fmtAmount(value, currency);
  const equiv = equivalentFor(value, currency, { rate, showThb });
  const canonicalCls = strong ? "font-semibold text-slate-900" : muted ? "text-slate-400" : "text-slate-700";
  if (layout === "inline") {
    return (
      <span className={`whitespace-nowrap ${className}`} data-testid={testid}>
        <span className={canonicalCls}>{canonical}</span>
        {equiv && <span className="ml-2 text-xs text-slate-400">≈ {equiv}</span>}
      </span>
    );
  }
  return (
    <span className={`inline-flex flex-col items-end leading-tight ${className}`} data-testid={testid}>
      <span className={canonicalCls}>{canonical}</span>
      {equiv && <span className="text-[11px] text-slate-400">≈ {equiv}</span>}
    </span>
  );
}

// Two-column table cell pair (Canonical Price | Equivalent) — for tabular pricing layouts.
export function DualPriceCells({ value, currency = "USD", rate, showThb, strong = true }) {
  const equiv = equivalentFor(value, currency, { rate, showThb });
  return (
    <>
      <td className={`px-3 py-2 text-right ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {fmtAmount(value, currency)} <span className="text-[11px] text-slate-400">{currency}</span>
      </td>
      <td className="px-3 py-2 text-right text-xs text-slate-400">{equiv || "—"}</td>
    </>
  );
}

export function FxDisclaimer({ className = "", fxSource = null, rateAsOf = null }) {
  const rateLabel = fxSource === "manual_fallback"
    ? "Manual rate set by your advisor"
    : rateAsOf
      ? `Current reference rate · as of ${formatRateDate(rateAsOf)}`
      : null;
  return (
    <div className={`space-y-0.5 ${className}`} data-testid="fx-disclaimer">
      {rateLabel && (
        <p className="text-[11px] text-slate-400" data-testid="fx-rate-attribution">{rateLabel}</p>
      )}
      <p className="text-[11px] text-slate-400 italic">{FX_DISCLAIMER}</p>
    </div>
  );
}
